/*
 * Copyright (c) 2021 CalAmp Corp.  All Rights Reserved
 */
import React, {ReactNode} from 'react';
import Grid from '@material-ui/core/Grid';
import {makeStyles} from '@material-ui/core/styles';
import classnames from 'classnames';
import {DLSTheme} from '../../types/types';

export interface FormItemType {
  children: ReactNode;
  fullWidth?: boolean;
  halfWidth?: boolean;
  thirdWidth?: boolean;
  customWidth?: 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10 | 11 | 12;
  disableMinWidth?: boolean;
  className?: string;
};

const useStyles = makeStyles((theme: DLSTheme) => ({
  formItem : {
    animationName                : '$fadeIn',
    animationDuration            : '1s',
    animationFillMode            : 'both',
    animationTimingFunction      : 'cubic-bezier(0.215, 0.61, 0.355, 1)',
    [theme.breakpoints.up('lg')] : {
      minWidth : ({disableMinWidth}: {disableMinWidth: boolean}) => disableMinWidth ? 'auto' : '400px'
    },
    [theme.breakpoints.down('sm')] : {
      minWidth  : 'none',
      maxWidth  : 'none',
      flexBasis : '100%'
    }
  },
  '@keyframes fadeIn' : {
    '0%'   : {opacity: 0},
    '100%' : {opacity: 1}
  }
}));

/**
 * @FormItem
 * Component that wraps its child component(s) with styles that are specific to a Form.
 *
 * @param {Node} children Component that gets wrapped
 * @param {boolean} fullWidth Should the grid item be full width; takes precedence
 *                            over halfWidth, thirdWidth and width if specified.
 * @param {boolean} halfWidth Should the grid item be half width (default); takes
 *                            precedence over thirdWidth and width if specified.
 * @param {boolean} thirdWidth Should the grid item be third width; takes precedence
 *                             over width if specified.
 * @param {number} customWidth Should the grid item be a specific custom width.
 * @param {boolean} disableMinWidth Should be specified if any of the above width
 *                                  properties are specified.
 *
 * ```typescript
 *
 * import {FormItem} from '@ion/dls/src/components/forms/FormItem';
 *
 * <FormItem fullWidth>
 *   <Component />
 * </FormItem>
 *
 * ```
 */
export const FormItem = ({
  children,
  fullWidth = false,
  halfWidth = false,
  thirdWidth = false,
  customWidth = 6,
  disableMinWidth = false,
  className,
  ...props
}: FormItemType) => {
  const classes = useStyles({disableMinWidth});
  const md = fullWidth ? 12 : (halfWidth ? 6 : (thirdWidth ? 4 : customWidth));
  const xs = 12;
  return (
    <Grid item
      md={md} xs={xs}
      classes={{
        root : className ? classnames(classes.formItem, className) : classes.formItem
      }}>
      {children}
    </Grid>
  );
};

export default FormItem;
